
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { SystemNotificationApi } from '../../../api/systemNotifications/system-notification.api';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { SystemNotificationModel } from '../models/system-notification.model';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { AuditableFilterService } from '../../../Common/Filter/services/auditable-filter.service';
import { UsersService } from '../../../Core/Users/services/users.service';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { FilterTypeDateQueryModel } from '../../../Common/Filter/models/filter-type-date-query.model';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { MessageService } from 'primeng/api';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { SystemNotificationsStatusEnum } from '../enums/system-notifications-status.enum';
import { getDefaultDateInputOptionValuesModel } from '../../../Common/Forms/models/date-query-input-options.model';

@Injectable()
export class SystemNotificationService extends AuditableFilterService {
    results: SystemNotificationModel[] = null;
    results$: BehaviorSubject<SystemNotificationModel[]> = new BehaviorSubject<SystemNotificationModel[]>(this.results);
    focusedNotification: SystemNotificationModel = null;
    focusedNotification$: BehaviorSubject<SystemNotificationModel> = new BehaviorSubject<SystemNotificationModel>(this.focusedNotification);

    reloadRequest: Observable<SystemNotificationModel[]>;
    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private readonly statusFilter: FilterTypeCheckboxModel = new FilterTypeCheckboxModel(
        'systemNotifications.statusFilter',
        'SystemNotification.filters.status',
        []
    );
    private readonly typeFilter: FilterTypeCheckboxModel = new FilterTypeCheckboxModel(
        'systemNotifications.typeFilter',
        'SystemNotification.filters.type',
        []
    );
    private readonly startDateFilter: FilterTypeDateQueryModel = new FilterTypeDateQueryModel(
        'systemNotifications.startDateFilter',
        'SystemNotification.filters.startDate',
        getDefaultDateInputOptionValuesModel()
    );
    readonly oneDayInMiliSec = 1000 * 60 * 60 * 24;

    private allFilters: FilterTypeModel[] = [
        this.statusFilter,
        this.typeFilter,
        this.startDateFilter,
        this.createdByFilter
    ];

    constructor(
        private systemNotificationApi: SystemNotificationApi,
        private coreDialogService: CoreDialogService,
        private messageService: MessageService,
        public userService: UsersService,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super(userService);

    }

    getLoading(): Observable<boolean> {
        return this.loading$;
    }

    getNotification(id: number): Observable<SystemNotificationModel> {
        return this.systemNotificationApi.get(id).pipe(map(response => new SystemNotificationModel(response)));
    }

    reloadList(): Observable<SystemNotificationModel[]> {
        if (!this.reloadRequest) {
            this.currentUserStateService.loadUserState();
            this.loading$.next(true);
            this.reloadRequest = this.systemNotificationApi.getList().pipe(map(items => {
                this.results = items.map(item => new SystemNotificationModel(item));
                this.results.forEach(result => {
                    const now = new Date().getTime();
                    if (result.statusBadge.status === SystemNotificationsStatusEnum.pending) {
                        const interval = result.notificationTimeRange.startConvertedwithTz.getTime() - now;
                        // If the timeout is longer than a day, don't bother setting it
                        // setting a timeout of greater than 2147483647 results in an overflow and an instant timeout
                        if ((interval <= this.oneDayInMiliSec) && (interval > 0))  {
                            setTimeout(() => this.reloadList(), interval);
                        }
                    }
                    if (result.statusBadge.status === SystemNotificationsStatusEnum.active) {
                        const interval = result.notificationTimeRange.endConvertedwithTz.getTime() - now;
                        if ((interval <= this.oneDayInMiliSec) && (interval > 0)) {
                            setTimeout(() => this.reloadList(), interval);
                        }
                    }
                });

                // apply filters to results
                this.setNotificationFilters();
                this.applyFilters();
                this.loading$.next(false);
                this.reloadRequest = null;
                return this.results;
            }));

            this.reloadRequest.subscribe();
        }
        return this.reloadRequest;
    }

    getNotificationList(): Observable<SystemNotificationModel[]> {
        if (!this.results) {
            this.reloadList();
        }
        return this.results$;
    }

    setFocusedNotification(notification: SystemNotificationModel) {
        this.focusedNotification = notification;
        this.focusedNotification$.next(notification);
    }

    getFocusedNotification(): Observable<SystemNotificationModel> {
        return this.focusedNotification$;
    }

    createNewNotification(notification: SystemNotificationModel): Observable<number> {
        return this.systemNotificationApi.create(notification.toApiModel());
    }

    updateNotification(notification: SystemNotificationModel): Observable<number> {
        return this.systemNotificationApi.update(notification.toApiModel());
    }

    deleteNotifications(deleteItems: SystemNotificationModel[]) {
        const confirmDialogModel: ConfirmDialogModel = {
            inputs: {
                heading: 'SystemNotification.deleteConfirmDialog.heading',
                showCancel: true,
                message: this.localizationService.transform('SystemNotification.deleteConfirmDialog.message', { count: deleteItems.length }),
                list: deleteItems.map(item => item.title),
                okLabel: 'SystemNotification.action.delete'
            },
            outputs: {
                dialogClose: (confirm: boolean) => {
                    if (confirm) {
                        this.loading$.next(true);
                        combineLatest(deleteItems.map(item => this.systemNotificationApi.delete(item.id))).subscribe({
                            next: () => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: 'SystemNotification.delete.success.heading',
                                    detail: 'SystemNotification.delete.success.message',
                                });
                                this.reloadList();
                            },
                            error: () => {
                                this.loading$.next(false);
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'SystemNotification.delete.error.heading',
                                    detail: 'SystemNotification.delete.error.message',
                                });
                            }
                        });
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
    }

    /**
     *  FILTER REGION
     *
     */

    setNotificationFilters() {
        this.setFilters(this.allFilters);

        this.getAuditableFilterOptions(this.results);

        // get status filter options
        this.getStringOptions(this.statusFilter, this.results, (item: SystemNotificationModel) => item.statusBadge.status, 'SystemNotification.status.');

        // get type filter options
        this.getStringOptions(this.typeFilter, this.results, (item: SystemNotificationModel) => item.type, 'SystemNotification.type.');
    }

    applyFilters() {
        super.applyFilters();
        let filteredResults = this.results;
        if (!filteredResults) {
            this.results$.next(filteredResults);
            return;
        }

        filteredResults = this.applyStartDateFilter(filteredResults);

        // apply created by filter
        if (this.createdByFilter.getValue().length > 0) {
            filteredResults = this.applyAuditableFilters(filteredResults);
        }

        // apply status filter
        if (this.statusFilter.getValue().length > 0) {
            filteredResults = this.applyStringOptions(this.statusFilter, filteredResults, (item: SystemNotificationModel) => item.statusBadge.status);
        }

        // apply type filter
        if (this.typeFilter.getValue().length > 0) {
            filteredResults = this.applyStringOptions(this.typeFilter, filteredResults, (item: SystemNotificationModel) => item.type);
        }

        this.results$.next(filteredResults);
    }

    private getStringOptions<SystemNotificationModel>(
        filter: FilterTypeCheckboxModel<string>,
        items: SystemNotificationModel[],
        getItemOptionKey: (item: SystemNotificationModel) => string,
        localePrefix: string
    ): void {
        if (!items || items.length < 1) {
            filter.options = [];
            return;
        }
        const optionCounts: {[key: string]: number} = {};
        items.forEach(item => {
            const key = getItemOptionKey(item);

            if (optionCounts[key]) {
                optionCounts[key]++;
            } else {
                optionCounts[key] = 1;
            }
        });
        const keys = Object.keys(optionCounts);
        filter.options = keys.map(key => {
            return {
                label: localePrefix + key,
                count: optionCounts[key],
                value: key
            };
        }).sort((a, b) => b.count - a.count);


        const filtersChanged = this.removeUnavailableOptions(filter);
        if (filtersChanged) {
            this.applyFilters();
        }
    }

    private applyStringOptions<SystemNotificationModel>(
        filter: FilterTypeCheckboxModel<string>,
        items: SystemNotificationModel[],
        getItemOptionKey: (item: SystemNotificationModel) => string
    ): SystemNotificationModel[] {
        const filterKeys = filter.getValue();

        if (!filterKeys || filterKeys.length < 1) {
            return items;
        }
        return items.filter((item) => {
            return filterKeys.includes(getItemOptionKey(item));
        });
    }

    private applyStartDateFilter(items: SystemNotificationModel[]): SystemNotificationModel[] {
        return items.filter(item => {
            return DateQueryModel.isInDateQuery(item.notificationTimeRange.startConvertedwithTz, this.startDateFilter.getValue(), new Date(), 30);
        });
    }

}
